import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useMatches,
  useRouter,
  useLocation,
} from "@tanstack/react-router";
import { MobileBottomNav } from "../components/shared/MobileBottomNav";
import { MentalHealthSupportPanel } from "../components/shared/MentalHealthSupportPanel";
import { SiteHeader } from "../components/shared/SiteHeader";
import { RAISE_IT_FOOTER_URL } from "../components/shared/raise-it-links";


function NotFoundComponent() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Page Not Found — Warren-Blackwood";
    return () => { document.title = prev; };
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/**
 * SPA replacement for TanStack Start's <HeadContent />. Walks the matched
 * routes and applies any title/meta declared via the route's `head()` option
 * to the live document.
 */
function HeadSync() {
  const router = useRouter();
  const matches = useMatches();

  useEffect(() => {
    let title: string | undefined;
    type MetaTag = { name?: string; property?: string; content?: string; title?: string };
    const metaTags: MetaTag[] = [];

    for (const match of matches) {
      const route = router.routesById[match.routeId as keyof typeof router.routesById] as
        | { options?: { head?: (ctx: unknown) => { meta?: MetaTag[] } } }
        | undefined;
      const head = route?.options?.head;
      if (typeof head !== "function") continue;
      const result = head({
        loaderData: (match as { loaderData?: unknown }).loaderData,
        params: (match as { params?: unknown }).params,
      });
      if (!result?.meta) continue;
      for (const tag of result.meta) {
        if (tag.title) title = tag.title;
        else metaTags.push(tag);
      }
    }

    if (title) document.title = title;

    // Manage meta tags we own with a data-attribute marker so we can clean
    // them up on subsequent navigations without touching unrelated tags.
    const owned = document.head.querySelectorAll('meta[data-head-sync="1"]');
    owned.forEach((el) => el.remove());
    for (const tag of metaTags) {
      if (!tag.content) continue;
      const el = document.createElement("meta");
      if (tag.name) el.setAttribute("name", tag.name);
      if (tag.property) el.setAttribute("property", tag.property);
      el.setAttribute("content", tag.content);
      el.setAttribute("data-head-sync", "1");
      document.head.appendChild(el);
    }
  }, [matches, router]);

  return null;
}

function Footer() {
  const location = useLocation();
  const profileActive = location.pathname === '/profile' || location.pathname.startsWith('/profile/');

  const colHeading: React.CSSProperties = {
    fontFamily: 'var(--font-sub)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.72rem',
    color: 'var(--yellow)',
    marginBottom: '0.5rem',
  };
  const linkRow: React.CSSProperties = {
    display: 'block',
    padding: '0.2rem 0',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  };

  return (
    <footer role="contentinfo" className="site-footer">
      <div className="site-footer__inner" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="site-footer__name">Warren-Blackwood Electorate Guide</div>
          <div className="site-footer__tagline">A constituent resource for WA's south-west.</div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'var(--sp-md)',
            width: '100%',
          }}
        >
          <div>
            <div style={colHeading}>Raise with Bevan</div>
            <a href="tel:+61898483171" style={linkRow}>(08) 9848 3171</a>
            <a href="mailto:bevan.eatts@mp.wa.gov.au" style={linkRow}>
              bevan.eatts@mp.wa.gov.au
            </a>
            <Link to="/contact" style={linkRow}>Office hours & how to help →</Link>
            <a
              href={RAISE_IT_FOOTER_URL}
              className="cta-raise-it cta-raise-it--footer"
              data-cta="raise-it-footer"
              style={{ marginTop: '0.6rem' }}
            >
              Raise it with Bevan
            </a>
          </div>

          <div>
            <div style={colHeading}>This site</div>
            <Link to="/" style={linkRow}>Map</Link>
            <Link
              to="/profile"
              aria-current={profileActive ? 'page' : undefined}
              style={{
                ...linkRow,
                ...(profileActive
                  ? { fontWeight: 700, textDecoration: 'underline', textDecorationColor: 'var(--yellow)' }
                  : {}),
              }}
            >
              Community profile
            </Link>
            <Link to="/about" style={linkRow}>About</Link>
            <Link to="/about" hash="data-sources" style={linkRow}>
              Data sources
            </Link>
            <Link to="/accessibility" style={linkRow}>
              Accessibility statement
            </Link>
          </div>

          <div>
            <div style={colHeading}>Official sources</div>
            <a
              href="https://www.parliament.wa.gov.au/Parliament/Memblist.nsf/Member+Lookup+LA+Current/Eatts,+Mr+Bevan?opendocument="
              style={linkRow}
            >
              Bevan's WA Parliament page
            </a>
            <a href="https://nationalswa.com/" style={linkRow}>The Nationals WA</a>
            <a href="https://www.elections.wa.gov.au/" style={linkRow}>
              WA Electoral Commission
            </a>
            <a href="https://www.elections.wa.gov.au/enrol" style={linkRow}>
              Check or update your enrolment
            </a>
          </div>
        </div>
      </div>
      <div className="site-footer__bottom">
        Published by the electorate office of Bevan Eatts MLA, Member for Warren-Blackwood. Data:
        ABS 2021 Census · WAEC · Shire council websites · WA Parliament.
      </div>
    </footer>
  );
}

/**
 * Global rule: every external link opens in a new tab.
 *
 * Centralised here so individual components don't need to remember to set
 * `target="_blank" rel="noopener noreferrer"` on every anchor. We:
 *   1. Sweep the DOM on mount + on each mutation, normalising any
 *      `<a href="http(s)://...">` whose host differs from window.location.
 *   2. Intercept clicks at the capture phase as a belt-and-braces fallback
 *      for anchors added between the observer firing and the click.
 *
 * `mailto:`, `tel:`, in-page (#hash) and same-origin links are left alone.
 */
function ExternalLinksNewTab() {
  useEffect(() => {
    const isExternal = (a: HTMLAnchorElement) => {
      if (a.hasAttribute('data-same-tab')) return false;
      const href = a.getAttribute('href');
      if (!href) return false;
      if (/^(mailto:|tel:|sms:|#|\/)/i.test(href)) return false;
      try {
        const url = new URL(href, window.location.href);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
        return url.host !== window.location.host;
      } catch {
        return false;
      }
    };

    const normalise = (a: HTMLAnchorElement) => {
      if (!isExternal(a)) return;
      if (a.getAttribute('target') !== '_blank') a.setAttribute('target', '_blank');
      const rel = (a.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean);
      for (const token of ['noopener', 'noreferrer']) {
        if (!rel.includes(token)) rel.push(token);
      }
      a.setAttribute('rel', rel.join(' '));
    };

    const sweep = (root: ParentNode) => {
      root.querySelectorAll('a[href]').forEach((el) => normalise(el as HTMLAnchorElement));
    };

    sweep(document);

    const observer = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = n as Element;
          if (el.tagName === 'A') normalise(el as HTMLAnchorElement);
          else sweep(el);
        });
        if (r.type === 'attributes' && r.target instanceof HTMLAnchorElement) {
          normalise(r.target);
        }
      }
    });
    // Only watch for added nodes. Attribute mutations across the whole
    // <body> subtree (map hover, zoom transitions, filter chips toggling
    // aria-pressed, etc.) flood the observer and stall the main thread.
    // The capture-phase click handler below catches any anchor whose href
    // is rewritten after mount, which is the only case the attribute
    // watch was defending against.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (a) normalise(a);
    };
    document.addEventListener('click', onClick, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', onClick, true);
    };
  }, []);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <HeadSync />
      <ExternalLinksNewTab />
      <SiteHeader />
      <Outlet />
      <MentalHealthSupportPanel />
      <Footer />
      <MobileBottomNav />
    </QueryClientProvider>

  );
}
